
import React, { useState, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import Chip from "@mui/material/Chip";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import { api } from "../../utils/axios-instance";
import useAuth from "../../stores/authStore";
import FileShare from "./FileShare";
import toast from "react-hot-toast";

const ReadIcon = `${process.env.PUBLIC_URL}/assets/read.webp`;

const FileInfo = ({ fileInfo, closeDrawer, sharedFileInfo, handleLockChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [Logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLockedLocal, setIsLockedLocal] = useState(false);

  const profileData = useAuth((state) => state.profileData);

  useEffect(() => {
    setIsLockedLocal(fileInfo?.isLocked || false);
    openDialog();
    getLogs();
  }, []);

  const getLogs = async () => {
    try {
      const accessLogs = await api.get(`/file/getLogs/${fileInfo.id}?recs=5`);
      let logsData = accessLogs.data;
      if (!Array.isArray(logsData)) {
        logsData = logsData?.logs || logsData?.data || [];
      }
      setLogs(logsData);
      setLoading(false);
    } catch (error) {
      if (error?.response?.status !== 404 && error?.response?.status !== 400) {
        // Silently ignore 400 and 404 errors as backend throws these when no logs exist
        toast.error(`Fetch error [500]: ${error?.response?.data?.error || error?.message}`);
      }
      console.log("getLogs error:", error);
      setLoading(false);
    }
  };

  const openDialog = () => setIsOpen(true);

  const closeDialog = () => {
    setIsOpen(false);
    setTimeout(closeDrawer, 300); // let drawer transition finish
  };

  const formatTimestamp = (timestamp) => {
    const options = {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    };
    return new Date(timestamp).toLocaleString("en-IN", options);
  };

  const getReadableType = (mimeType) => {
    if (!mimeType) return "Unknown";
    if (mimeType.includes("pdf")) return "PDF Document";
    if (mimeType.includes("word") || mimeType.includes("document")) return "Word Document";
    if (mimeType.includes("excel") || mimeType.includes("spreadsheet")) return "Excel Spreadsheet";
    if (mimeType.includes("image")) return "Image File";
    return mimeType;
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={closeDialog}
      PaperProps={{
        sx: { width: 400, backgroundColor: "#FFFFFF" },
      }}
    >
      <div className="flex flex-col h-full">
        {/* HEADER */}
        <div className="flex justify-between items-center p-4">
          <h2 className="text-lg font-bold text-gray-800 line-clamp-1">
            {fileInfo?.name?.split("_TS=")[0] || "File Details"}
          </h2>
          <IconButton onClick={closeDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/* SCROLLABLE CONTENT */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 custom-scrollbar">

          {/* FILE INFO */}
          <div className="mb-6">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
              File Info
            </p>
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Type</span>
                <span className="font-medium text-gray-800 text-right">
                  {getReadableType(fileInfo?.mimetype)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Size</span>
                <span className="font-medium text-gray-800 text-right">
                  {fileInfo?.size || "-"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Storage Path</span>
                <span className="font-medium text-gray-800 text-right line-clamp-1 max-w-[200px]">
                  {fileInfo?.name || "-"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Owner</span>
                <span className="font-medium text-gray-800 text-right line-clamp-1 max-w-[200px]">
                  {fileInfo?.owner || "-"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Organization</span>
                <span className="font-medium text-gray-800 text-right">
                  {profileData?.org || profileData?.org_id || "twokey"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Folder</span>
                <span className="font-medium text-gray-800 text-right">
                  {fileInfo?.folder_id || "-"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Departments</span>
                <span className="font-medium text-gray-800 text-right">
                  {fileInfo?.department || "-"}
                </span>
              </div>
            </div>
          </div>

          <Divider sx={{ mb: 4 }} />

          {/* VERSIONS */}
          <div className="mb-6">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
              Versions
            </p>
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Current</span>
                <span className="font-medium text-gray-800">
                  {fileInfo?.versionLabel || "v1"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">History</span>
                <span className="font-medium text-gray-800">
                  {fileInfo?.versions?.length || 0} versions
                </span>
              </div>
              {fileInfo?.versions && fileInfo.versions.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {fileInfo.versions.map((ver, idx) => (
                    <Chip
                      key={idx}
                      label={`v${idx + 1}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ borderRadius: "6px", backgroundColor: "#EEF2FF", border: "none", color: "#4F46E5", fontWeight: "bold" }}
                    />
                  ))}
                  <Chip
                    label={fileInfo?.versionLabel || `v${fileInfo.versions.length + 1}`}
                    size="small"
                    color="primary"
                    sx={{ borderRadius: "6px", backgroundColor: "#4F46E5", color: "white", fontWeight: "bold" }}
                  />
                </div>
              )}
            </div>
          </div>

          <Divider sx={{ mb: 4 }} />

          {/* ACCESS */}
          <div className="mb-6">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
              Access
            </p>
            <div className="flex flex-col gap-3">
              <div className="flex justify-between items-center">
                <div className="flex flex-col">
                  <span className="text-sm text-gray-800 font-medium">Locked</span>
                  <span className="text-xs text-gray-500">
                    {isLockedLocal ? "Requires password or privileges" : "No password"}
                  </span>
                </div>
                <Switch
                  checked={isLockedLocal}
                  onChange={() => {
                    setIsLockedLocal(!isLockedLocal);
                    if (handleLockChange) handleLockChange();
                  }}
                  color="primary"
                />
              </div>

              <FileShare
                menuFile={fileInfo}
                closeMenu={() => { }}
                customTrigger={
                  <div className="flex justify-between items-center w-full">
                    <div className="flex flex-col text-left">
                      <span className="text-sm text-gray-800 font-medium">Shared</span>
                      <span className="text-xs text-gray-500">
                        {sharedFileInfo?.shared_with?.length > 0 ? "Shared with users" : "Not shared"}
                      </span>
                    </div>
                    <Switch
                      checked={sharedFileInfo?.shared_with?.length > 0 || false}
                      color="primary"
                      disableRipple
                      sx={{ pointerEvents: 'none' }}
                    />
                  </div>
                }
              />
            </div>
          </div>

          <Divider sx={{ mb: 4 }} />

          {/* PINNED BY */}
          <div className="mb-6">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
              Pinned By
            </p>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Pins</span>
              <span className="font-medium text-gray-800 text-right">
                {fileInfo?.isPinned ? "You" : "Not pinned"}
              </span>
            </div>
          </div>

          <Divider sx={{ mb: 4 }} />

          {/* DUE DATE */}
          <div className="mb-6">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
              Due Date
            </p>
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Date</span>
                <span className="font-medium text-gray-800 text-right">
                  {fileInfo?.dueDate || fileInfo?.due_date || "-"}
                </span>
              </div>
            </div>
          </div>

          <Divider sx={{ mb: 4 }} />

          {/* ACCESS LOGS */}
          <div className="mb-4">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
              Access Logs
            </p>
            <div className="max-h-60 overflow-y-auto custom-scrollbar">
              {loading ? (
                <p className="text-center text-sm text-gray-500 py-4">loading logs...</p>
              ) : (
                <Stepper activeStep={Logs.length - 1} orientation="vertical" sx={{ padding: "0 8px" }}>
                  {Logs.length > 0 ? (
                    Logs.map((log, index) => (
                      <Step key={index}>
                        <StepLabel
                          icon={
                            <img
                              src={ReadIcon}
                              alt="read"
                              className="bg-indigo-100 rounded-full p-1 w-6 h-6"
                            />
                          }
                        >
                          <div>
                            <p className="text-xs tracking-wide">
                              <strong className="text-gray-800">{log.username}</strong>{" "}
                              <span className="text-gray-500">
                                {log.event === "screenshot"
                                  ? "took screenshot of"
                                  : "accessed"}
                              </span>{" "}
                              <span className="text-indigo-500 font-medium">file</span>
                            </p>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                              {formatTimestamp(log.timestamp)}
                            </p>
                          </div>
                        </StepLabel>
                      </Step>
                    ))
                  ) : (
                    <Step key={0}>
                      <StepLabel icon={<div className="w-2 h-2 rounded-full bg-gray-300 ml-2" />}>
                        <p className="text-center text-sm text-gray-500">No logs found!</p>
                      </StepLabel>
                    </Step>
                  )}
                </Stepper>
              )}
            </div>
          </div>

        </div>
      </div>
    </Drawer>
  );
};

export default FileInfo;
